import { useMemo } from 'react';

import { SCHOOL_POST_TYPE } from '@jebel/constants';
import { createFilterBuilder } from '@jebel/utils';

import { HomeFeedItemFilter } from 'shared/graphql';

import { useCurrentUserContentRestrictions } from './useCurrentUserContentRestrictions';

/**
 * Returns a filter for home feed items based on the current user's content restrictions.
 * The filter restricts posts to those whose author affiliation matches the allowed creator affiliations
 * from the user's active content restrictions.
 *
 * @returns {HomeFeedItemFilter} A filter object to be used in home feed queries.
 */
export function useCurrentUserContentRestrictionsFilter(): HomeFeedItemFilter {
  const { data: restrictions } = useCurrentUserContentRestrictions();

  return useMemo(() => {
    const filter = createFilterBuilder<HomeFeedItemFilter>();

    const creatorAffiliations = new Set<string>(
      restrictions.flatMap(restriction => restriction.scope.creatorAffiliations),
    );

    if (creatorAffiliations.size > 0) {
      // Filter by creator affiliations
      // https://github.com/jebelapp/jebel/issues/1842
      filter.and({
        OR: [
          { authorAffiliation: { in: Array.from(creatorAffiliations) } },
          // Allow posts without an affiliation to be displayed.
          { authorAffiliation: { is_empty: true } },
          // Allow school posts to always be displayed.
          { postType: { equals: SCHOOL_POST_TYPE } },
        ],
      });
    }

    return filter.build();
  }, [restrictions]);
}
